import React from 'react';

/**
 * PrivacyPage component
 * Privacy Policy page for the healthcare AI platform
 */
const PrivacyPage = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Privacy <span style={{ color: '#FFC000' }}>Policy</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Your privacy and data protection are our top priorities.
            </p>
            
            <p className="text-gray-600">
              Last updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg dark:prose-invert">
            
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Introduction</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Videxa ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our healthcare AI platform and related services.
            </p>

            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Personal Information</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              We may collect personal information that you provide directly to us, including:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 mb-6 space-y-2">
              <li>Name, email address, and contact information</li>
              <li>Professional credentials and organization details</li>
              <li>Account login credentials</li>
              <li>Payment and billing information</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Healthcare Data</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              As a healthcare AI platform, we process healthcare-related data in accordance with HIPAA and other applicable regulations. This may include de-identified patient data, claims information, and clinical workflow data.
            </p>

            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Usage Data</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We automatically collect information about how you use our platform, including access logs, feature usage, and system performance metrics.
            </p>

            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">How We Use Your Information</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 mb-6 space-y-2">
              <li>Provide and maintain our healthcare AI services</li>
              <li>Process and analyze healthcare data for AI model improvements</li>
              <li>Ensure platform security and prevent unauthorized access</li>
              <li>Communicate with you about services and updates</li>
              <li>Comply with legal and regulatory requirements</li>
            </ul>

            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Data Protection and Security</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              We implement robust security measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 mb-6 space-y-2">
              <li>End-to-end encryption for data in transit and at rest</li>
              <li>Multi-factor authentication and access controls</li>
              <li>Regular security audits and penetration testing</li>
              <li>HIPAA-compliant infrastructure and processes</li>
              <li>Employee training on data protection protocols</li>
            </ul>

            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Data Sharing and Disclosure</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              We do not sell your personal information. We may share information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 mb-6 space-y-2">
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or court orders</li>
              <li>With trusted service providers under strict confidentiality agreements</li>
              <li>In case of business transfers (mergers, acquisitions)</li>
            </ul>

            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Your Rights</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 mb-6 space-y-2">
              <li>Access: Request access to your personal information</li>
              <li>Correction: Request correction of inaccurate information</li>
              <li>Deletion: Request deletion of your personal information</li>
              <li>Portability: Request transfer of your data to another service</li>
              <li>Objection: Object to certain processing of your information</li>
            </ul>

            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Compliance</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Our platform complies with major healthcare regulations including HIPAA, GDPR, and state privacy laws. We maintain Business Associate Agreements (BAAs) with covered entities and regularly audit our compliance practices.
            </p>

            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <p className="text-gray-600 dark:text-gray-300">
                <strong>Privacy Officer</strong><br />
                Videxa<br />
                Email: privacy@videxa.com<br />
                Phone: 1-888-465-4246<br />
                Address: 123 Healthcare Innovation Drive, San Francisco, CA 94105
              </p>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mt-8">
              This Privacy Policy may be updated periodically. We will notify you of any material changes through our platform or via email.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;